import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { DirectorySearchItem, searchFlownectDirectory } from "../../../contacts/actions/searchDirectory.actions";
import { useGetContacts } from "../../../contacts/hooks/useContacts";
import { IContact } from "../../../types/contacts.types";
import { SmsContact } from "../types/SmsContact";

const SEARCH_DEBOUNCE_MS = 500;
const SEARCH_PAGE_SIZE = 50;
const SEARCH_ALL_CONTACT_TYPES = "";

const DIRECTORY_TYPE_TO_SMS: Record<DirectorySearchItem["type"], string> = {
  client: "CLIENT",
  workforce: "WORKFORCE",
  vendor: "VENDOR",
};

const toContactList = (res: unknown): IContact[] => {
  if (Array.isArray(res)) return res;
  if (res && typeof res === "object") {
    const withData = res as { data?: unknown; uid?: string };
    if (Array.isArray(withData.data)) return withData.data as IContact[];
    if (withData.uid) return [res as IContact];
  }
  return [];
};

const contactPhoneNumbers = (contact: IContact): string[] => {
  const fromList = (contact.phoneNumbers ?? []).map((phone) => phone?.number).filter(Boolean) as string[];
  if (fromList.length > 0) return fromList;
  return contact.phoneNumber ? [contact.phoneNumber] : [];
};

export const mapVrContactsToSmsContacts = (contacts: IContact[]): SmsContact[] => {
  const mapped: SmsContact[] = [];

  for (const contact of contacts) {
    for (const phoneNumber of contactPhoneNumbers(contact)) {
      mapped.push({
        phoneNumber,
        contact: {
          uid: contact.uid,
          name: contact.name,
          lastName: contact.lastName,
          firstName: contact.name,
          type: contact.type,
          source: "vr-assistant",
        },
      });
    }
  }

  return mapped;
};

export const mapDirectoryItemsToSmsContacts = (items: DirectorySearchItem[]): SmsContact[] => {
  const mapped: SmsContact[] = [];

  for (const item of items) {
    const type = DIRECTORY_TYPE_TO_SMS[item.type];
    const name = [item.firstName, item.lastName].filter(Boolean).join(" ") || item.businessName || "";

    for (const phoneNumber of item.phones ?? []) {
      mapped.push({
        phoneNumber,
        contact: {
          uid: item.uid,
          name,
          firstName: item.firstName,
          lastName: item.lastName,
          type,
          role: type,
          businessName: item.businessName ?? null,
          phone: phoneNumber,
          source: "flownect",
          isResolved: true,
        },
      });
    }
  }

  return mapped;
};

export const useSearchVrSmsContacts = (companyId: number, searchTerm: string) => {
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const hasSearch = Boolean(companyId && debouncedSearchTerm);

  const vrQuery = useGetContacts(companyId, 1, SEARCH_PAGE_SIZE, debouncedSearchTerm, {
    enabled: hasSearch,
    type: SEARCH_ALL_CONTACT_TYPES,
  });

  const directoryQuery = useQuery({
    queryKey: ["contact-center", "directory-search", debouncedSearchTerm],
    queryFn: () => searchFlownectDirectory(debouncedSearchTerm),
    enabled: hasSearch,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const apiContacts = useMemo(() => {
    if (!hasSearch) return [];

    return [...mapVrContactsToSmsContacts(toContactList(vrQuery.data)), ...mapDirectoryItemsToSmsContacts(directoryQuery.data ?? [])];
  }, [directoryQuery.data, hasSearch, vrQuery.data]);

  return {
    apiContacts,
    isSearching: hasSearch && (vrQuery.isFetching || directoryQuery.isFetching),
  };
};
